/**
 * Enhanced Form Handler with reCAPTCHA v2 integration
 * Features:
 * - Prevents multiple form submissions
 * - Handles reCAPTCHA verification
 * - Provides visual feedback during form submission
 * - Restores form state after timeout
 * - Adds better error handling
 */
export default () => {
  const config = {
    disabledDurationThreshold: 6000, // milliseconds to wait before re-enabling buttons after submission
    submitButtonLoadingText: 'Please wait...',
    recaptchaErrorText: 'Please complete the reCAPTCHA to submit form',
    errorTextColor: 'red',
    errorTextPadding: '5px 0',
  };

  // Store original button values to restore them later
  const originalButtonValues = new WeakMap();

  /**
   * Initialize a form with reCAPTCHA integration
   * @param {HTMLFormElement} form - The form to initialize
   */
  const initializeForm = (form) => {
    const recaptcha = form.querySelector('.g-recaptcha');
    const submitButtons = form.querySelectorAll('input[type=submit], button[type=submit]');

    // Only handle forms with reCAPTCHA
    if (!recaptcha) return;

    // Store original button values
    submitButtons.forEach(button => {
      originalButtonValues.set(button, button.value || button.textContent);
    });

    // Setup reCAPTCHA callbacks
    recaptcha.setAttribute('data-callback', 'recaptchaCallback');
    recaptcha.setAttribute('data-expired-callback', 'recaptchaExpiredCallback');
    recaptcha.setAttribute('data-error-callback', 'recaptchaErrorCallback');

    // Create error message element
    const errorDiv = document.createElement('div');
    errorDiv.className = 'recaptcha-error-message';
    errorDiv.innerText = config.recaptchaErrorText;
    errorDiv.style.color = config.errorTextColor;
    errorDiv.style.padding = config.errorTextPadding;
    errorDiv.style.display = 'none'; // Initially hidden
    recaptcha.parentNode.insertBefore(errorDiv, recaptcha.nextSibling);

    // Initially disable submit buttons
    toggleSubmitButtons(submitButtons);

    // Setup form submission handler
    form.addEventListener('submit', (event) => {
      // Verify recaptcha is still valid
      const recaptchaResponse = grecaptcha.getResponse();
      if (!recaptchaResponse) {
        event.preventDefault();
        errorDiv.style.display = 'block';
        return false;
      }

      if (!form.checkValidity()) {
        return true;
      }

      // Disable submit buttons upon submission
      submitButtons.forEach(button => {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('data-disabled-timestamp', Date.now().toString());

        // Set loading text
        if (button.tagName === 'INPUT') {
          button.value = config.submitButtonLoadingText;
        } else {
          button.textContent = config.submitButtonLoadingText;
        }
      });

      // add form submitting attribute
      form.setAttribute('data-submitting', 'true');

      // Allow form submission to proceed
      return true;
    });
  };


  /**
   * Toggle the disabled state of submit buttons
   * @param {NodeList} buttons - The buttons to disable/enable
   * @param {boolean} disable - Whether to enable or disable the buttons
   */
  const toggleSubmitButtons = (buttons, disable = true) => {
    buttons.forEach(button => {
      if (disable) {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('data-disabled-timestamp', Date.now().toString());
      } else {
        button.removeAttribute('disabled');
        button.removeAttribute('data-disabled-timestamp');
      }
    });
  }

  /**
   * Reset buttons to their original state
   * @param {NodeList} buttons - The buttons to reset
   */
  const resetButtons = (buttons) => {
    buttons.forEach(button => {
      if (originalButtonValues.has(button)) {
        if (button.tagName === 'INPUT') {
          button.value = originalButtonValues.get(button);
        } else {
          button.textContent = originalButtonValues.get(button);
        }
      }
      button.removeAttribute('disabled');
      button.removeAttribute('data-disabled-timestamp');
    });
  };

  /**
   * Check and restore forms that have been disabled for too long after submission
   * This is to handle cases where the form submission times out or fails
   */
  const restoreTimedOutForms = () => {
    document.querySelectorAll('form[data-submitting="true"]').forEach(form => {
      const recaptcha = form.querySelector('.g-recaptcha');
      const disabledButtons = form.querySelectorAll(
        'input[type=submit]:disabled[data-disabled-timestamp], button[type=submit]:disabled[data-disabled-timestamp]'
      );

      // Skip forms without recaptcha or disabled buttons
      if (!disabledButtons.length) {
        // No disabled buttons, so remove the submitting attribute
        form.removeAttribute('data-submitting');
        return;
      }

      disabledButtons.forEach(button => {
        const disabledTimestamp = parseInt(button.dataset.disabledTimestamp, 10);
        const disabledDuration = Date.now() - disabledTimestamp;

        // If button has been disabled long enough, reset it
        if (disabledDuration > config.disabledDurationThreshold) {
          if (recaptcha) {
            const errorMsg = form.querySelector('.recaptcha-error-message');
            if (errorMsg) {
              errorMsg.style.display = 'none';
            }
            // Reset the recaptcha too
            try {
              grecaptcha.reset();
            } catch (e) {
              console.warn('Could not reset reCAPTCHA:', e);
            }
          }

          resetButtons([button]);
          form.removeAttribute('data-submitting');
        }
      });
    });
  };

  /**
   * Toggle the visibility of the error message
   * @param {HTMLFormElement} form - The form containing the error message
   * @param {boolean} isError - Whether to show or hide the error message
   */
  const toggleErrorMessage = (form, isError) => {
    const errorDiv = form.querySelector('.recaptcha-error-message');
    if (errorDiv) {
      errorDiv.style.display = isError ? 'block' : 'none';
    }
  };

  // Initialize all forms on the page
  document.querySelectorAll('form').forEach(initializeForm);

  // Set up periodic check for timed-out forms
  setInterval(restoreTimedOutForms, Math.min(1000, config.disabledDurationThreshold / 2));

  // Handle reCAPTCHA error states
  const handleRecaptchaState = (CallbackType, isError = true) => {
    const recaptcha = document.querySelector(`.g-recaptcha${CallbackType}`);

    if (!recaptcha) return;

    const form = recaptcha.closest('form');
    if (!form) return;

    toggleErrorMessage(form, isError);
    toggleSubmitButtons(form.querySelectorAll('input[type=submit], button[type=submit]'), isError);
  }

  // Define global reCAPTCHA callback functions
  window.recaptchaCallback = function() {
    const response = grecaptcha.getResponse();
    if (!response) {
      console.error('reCAPTCHA response is empty');
      return;
    }

    handleRecaptchaState('[data-callback="recaptchaCallback"]', false);
  };

  window.recaptchaExpiredCallback = function() {
    handleRecaptchaState('[data-expired-callback="recaptchaExpiredCallback"]');
  };

  window.recaptchaErrorCallback = function() {
    handleRecaptchaState('[data-error-callback="recaptchaErrorCallback"]');
  };
};
